import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StepShell from "../components/StepShell";
import AyupathCard from "../components/AyupathCard";
import Field from "../components/Field";
import Select from "../components/Select";
import PrimaryButton from "../components/PrimaryButton";
import { useForm } from "../context/FormContext";
import { buildDobISO } from "../utils";
import useEnterToContinue from "../hooks/useEnterToContinue";

const TOTAL = 6;

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

function daysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

/**
 * The chosen date in words, with the age it gives, so a slip to the next
 * decade in the year list stands out before every date in the reading is
 * built on it. Null until the date is complete.
 */
export function describeDob(dob, today = new Date()) {
  const year = Number(dob.year);
  const month = Number(dob.month);
  const day = Number(dob.day);
  if (!year || !month || !day) return null;
  const birthdayPassed = today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);
  const age = today.getFullYear() - year - (birthdayPassed ? 0 : 1);
  return { date: `${day} ${MONTH_NAMES[month - 1]} ${year}`, age };
}

export default function StepDob() {
  const { formData, setFormData } = useForm();
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const list = [];
    for (let y = currentYear; y >= 1920; y--) list.push(y);
    return list;
  }, [currentYear]);

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const maxDays = daysInMonth(formData.dob.year, formData.dob.month);
  const days = useMemo(() => Array.from({ length: maxDays }, (_, i) => i + 1), [maxDays]);

  const dobISO = buildDobISO(formData.dob);
  const canContinue = Boolean(dobISO);
  const chosen = describeDob(formData.dob);

  useEnterToContinue(() => navigate("/birth-time"), canContinue);

  const updateDob = (patch) => {
    const next = { ...formData.dob, ...patch };
    // if day exceeds new month max, clamp
    const dmax = daysInMonth(next.year, next.month);
    if (next.day && Number(next.day) > dmax) next.day = dmax;
    setFormData({ ...formData, dob: next });
  };

  return (
    <StepShell step={2} totalSteps={TOTAL} title="Your next chapter">
      <AyupathCard>
        <div className="space-y-5">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">When were you born?</h2>
            <p className="text-gray-600 mt-1">Your birth date helps us calculate your personal chart.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Year">
              <Select
                value={formData.dob.year}
                onChange={(e) => updateDob({ year: e.target.value })}
              >
                <option value="">Year</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </Select>
            </Field>

            <Field label="Month">
              <Select
                value={formData.dob.month}
                onChange={(e) => updateDob({ month: e.target.value })}
              >
                <option value="">Month</option>
                {months.map((m) => <option key={m} value={m}>{MONTH_NAMES[m - 1]}</option>)}
              </Select>
            </Field>

            <Field label="Day">
              <Select
                value={formData.dob.day}
                onChange={(e) => updateDob({ day: e.target.value })}
              >
                <option value="">Day</option>
                {days.map((d) => <option key={d} value={d}>{String(d).padStart(2,"0")}</option>)}
              </Select>
            </Field>
          </div>

          {chosen ? (
            <p className="rounded-2xl bg-orange-50 border border-orange-100 px-4 py-3 text-gray-800" aria-live="polite">
              <strong className="text-gray-900">{chosen.date}</strong>
              {chosen.age >= 0 ? <> · you're {chosen.age} now</> : null}
              <span className="block text-sm text-gray-600">Please check the year: every date in your reading is built on it.</span>
            </p>
          ) : null}

          <PrimaryButton disabled={!canContinue} onClick={() => navigate("/birth-time")}>
            Next
          </PrimaryButton>
        </div>
      </AyupathCard>
    </StepShell>
  );
}
